import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import PageLayout from "@components/core/layouts/PageLayout";
import { Button, Checkbox, Form, Space } from "antd";
import { ROUTES } from "@routes/Routes";
import { useUnit } from "effector-react";
import { AuthenticationContext, UserOffice } from "@type/core/auth/auth.types";
import { authenticationStore } from "@state/auth/AuthStore";
import { Trans, useTranslation } from "react-i18next";
import CardSection from "@components/core/section/CardSection";
import { requestCreateOfficeCgu } from "@state/cgu/CguEffects";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import {
  CGU,
  CGU_TYPES,
  DPP,
  ECMA_DELEGATION_INFO_LINK,
} from "@utils/Constant";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import { CguOffice } from "@state/cgu/dto/request/cgu.body.request.dto";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { useNavigate } from "react-router-dom";
import "@styles/components/core/cguScreen.scss";
import { requestUpdateUserCgu } from "@/state/users/UserEffects";

const CguScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [checkStates, setCheckStates] = useState<Record<string, boolean>>({});

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const me = authStore?.user;
  const offices = me?.officesForCgu;

  useEffect(() => {
    const initialCheckStates: Record<string, boolean> = {};
    offices?.forEach((office) => {
      initialCheckStates[`cgu_${office.id}`] = office.cgu ?? false;
      initialCheckStates[`dpp_${office.id}`] = office.cgu ?? false;
    });
    setCheckStates(initialCheckStates);
  }, [offices]);

  useEffect(() => {
    return () => {
      // passage des user cgu à false en quittant cette page
      void requestUpdateUserCgu({});
    };
  }, []);

  const handleCheckboxChange = (
    officeId: string,
    type: string,
    checked: boolean,
  ) => {
    setCheckStates((prevState) => ({
      ...prevState,
      [`${type}_${officeId}`]: checked,
    }));
  };

  const isAllAccepted = () => {
    return Object.values(checkStates).every((checked) => checked);
  };

  const checkboxValue = (office: UserOffice, fieldsValue: any) => {
    const id = office.id;
    const cgu = fieldsValue[`cgu_${id}`];
    const dpp = fieldsValue[`dpp_${id}`];

    if (cgu === undefined && dpp === undefined) {
      return office.cgu;
    }
    if (cgu === false || dpp === false) {
      return false;
    }
    return true;
  };

  const showModal = () => {
    const fieldsValue = form.getFieldsValue();
    if (
      me?.officesForCgu
        .map((office) => office.expertComptable)
        .reduce((acc, cur) => acc || cur)
    ) {
      const officesForCgu: CguOffice[] | undefined = offices?.map((office) => ({
        acceptedCgu: checkboxValue(office, fieldsValue),
        name: office.name,
        id: office.id,
        alias: office.alias,
        cguDetail: office.cguDetail,
      }));
      const officeNames = officesForCgu
        ?.map((office) => `${office.name}, `)
        .toString();
      void requestCreateOfficeCgu({
        dto: {
          offices: officesForCgu,
        },
      })
        .then((response) => {
          if (response.ok && response.data) {
            toastSuccess(t("success.cguAccepted", { officeNames }));
          }
        })
        .catch((error) => {
          toastError(t("error.toast.cguAccepted", { officeNames }));
          console.error(
            "Error accepting CGU for office:",
            officesForCgu,
            error,
          );
        });
    }
    setIsModalVisible(true);
  };

  const cguAndDppAccepted = (office: UserOffice) => {
    return (
      office.checkList &&
      !office.checkList.some((checkItem) => !checkItem.checked)
    );
  };

  const isExpertComptableInAtLeastOneOffice = useMemo(
    () => offices?.some((office) => office.expertComptable) ?? false,
    [offices],
  );
  const hasAtLeastOneOfficeWithCguAccepted = useMemo(
    () => offices?.some((office) => office.cgu) ?? false,
    [offices],
  );
  const noOffices = !offices || offices.length === 0;
  const ecWithNoOffice = noOffices && !!me?.isExpertComptable;
  const collaborateurWithNoOffice = noOffices && !me?.isExpertComptable;
  const collaborateurOnlyWithNoCguAccepted =
    !isExpertComptableInAtLeastOneOffice && !hasAtLeastOneOfficeWithCguAccepted;
  const hasNoAcceptedCgu =
    ecWithNoOffice ||
    collaborateurWithNoOffice ||
    collaborateurOnlyWithNoCguAccepted;

  return (
    <PageLayout container>
      <>
        <CardSection
          title={t(
            `cgu.title.${
              ecWithNoOffice || collaborateurWithNoOffice
                ? "ecWithNoOffice"
                : collaborateurOnlyWithNoCguAccepted
                  ? "collaborateurWithNoCguAccepted"
                  : "default"
            }`,
          )}
        >
          <>
            {hasNoAcceptedCgu ? (
              <div className={"row d-block text-danger pl-5 pr-5"}>
                <p>
                  {ecWithNoOffice ? (
                    <Trans
                      i18nKey={"cgu.error.ecWithNoOffice"}
                      values={{
                        website: "Jesignexpert.com",
                      }}
                      components={[
                        // eslint-disable-next-line react/jsx-key
                        <u className="text-danger"></u>,
                      ]}
                    />
                  ) : collaborateurWithNoOffice ? (
                    <Trans
                      i18nKey={"login.messages.noOffices.message"}
                      values={{
                        website: "Jesignexpert.com",
                        delegationLink: "ici",
                      }}
                      components={[
                        // eslint-disable-next-line react/jsx-key
                        <u className="text-danger"></u>,
                        // eslint-disable-next-line react/jsx-key
                        <a
                          className="text-danger"
                          href={ECMA_DELEGATION_INFO_LINK}
                          target="_blank"
                          rel="noreferrer noopener"
                        />,
                      ]}
                    />
                  ) : (
                    t("cgu.error.collaborateurWithNoCguAccepted")
                  )}
                </p>
              </div>
            ) : (
              <Fragment>
                {me.firstTimeCgu && <p>{t("cgu.firstTime")}</p>}
                <p>{t("cgu.description")}</p>
                <p>
                  <strong>{t("cgu.descriptionButton")}</strong>
                </p>
              </Fragment>
            )}
            <Form form={form} layout="vertical" className="cgu-form">
              {offices?.map((office) => {
                const showCheckIcon =
                  office.cgu || (cguAndDppAccepted(office) && !office.cgu);
                return (
                  <Space
                    key={office.id}
                    direction="vertical"
                    className={"space"}
                  >
                    <div className={"office"}>
                      {showCheckIcon ? (
                        <CheckIcon className="icon-check" />
                      ) : (
                        <CrossIcon className="icon-cross" />
                      )}
                      <strong className={"officeName"}>{office.name}</strong>
                      {office.cguDetail && " (" + t("cgu.accepted.accepted")}
                      {office.cguDetail &&
                        office.cguDetail.acceptedBy !== null &&
                        t("cgu.accepted.by") + office.cguDetail.acceptedBy}
                      {office.cguDetail &&
                        t("cgu.accepted.date") +
                          office.cguDetail.acceptedDate +
                          ")"}
                    </div>
                    {!hasNoAcceptedCgu && (
                      <>
                        <Form.Item
                          name={`cgu_${office.id}`}
                          valuePropName="checked"
                          className={"checkbox"}
                          rules={[
                            {
                              validator: (_, value) =>
                                value
                                  ? Promise.resolve()
                                  : Promise.reject(t("cgu.notAccepted")),
                            },
                          ]}
                        >
                          <Checkbox
                            defaultChecked={office.cgu}
                            disabled={office.cgu || office.collaborateur}
                            onChange={(e) =>
                              handleCheckboxChange(
                                office.id,
                                CGU_TYPES.CGU,
                                e.target.checked,
                              )
                            }
                          >
                            {t("cgu.cgu")}
                            <a href={CGU.LINK} target="_blank" rel="noreferrer">
                              {CGU.LABEL}
                            </a>
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={`dpp_${office.id}`}
                          valuePropName="checked"
                          className={"checkbox"}
                          rules={[
                            {
                              validator: (_, value) =>
                                value
                                  ? Promise.resolve()
                                  : Promise.reject(t("cgu.notAccepted")),
                            },
                          ]}
                        >
                          <Checkbox
                            defaultChecked={office.cgu}
                            disabled={office.cgu || office.collaborateur}
                            onChange={(e) =>
                              handleCheckboxChange(
                                office.id,
                                CGU_TYPES.DPP,
                                e.target.checked,
                              )
                            }
                          >
                            {t("cgu.dpp")}
                            <a href={DPP.LINK} target="_blank" rel="noreferrer">
                              {DPP.LABEL}
                            </a>
                          </Checkbox>
                        </Form.Item>
                      </>
                    )}
                  </Space>
                );
              })}
            </Form>
            {!hasNoAcceptedCgu && (
              <div className={"saveButton"}>
                <Button
                  className="btn-tertiary m-8"
                  title={t("cgu.validate")}
                  onClick={showModal}
                  disabled={!isAllAccepted()}
                >
                  {t("cgu.validate")}
                </Button>
              </div>
            )}
          </>
        </CardSection>
        <ConfirmModal
          visible={isModalVisible}
          title={t("modal.cgu.title")}
          onOk={() => {
            navigate(
              ROUTES.extranet.menu.offices.balanceAndStatistics.generate(),
            );
          }}
          onCancel={() => {
            navigate(ROUTES.extranet.home.generate());
          }}
        ></ConfirmModal>
      </>
    </PageLayout>
  );
};

export default CguScreen;
